import { Component, ChangeDetectionStrategy, signal, afterNextRender, computed, inject, ElementRef, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ThemeService } from '../../services/theme.service';
import { TranslationService, LanguageCode } from '../../services/translation.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .menu-open {
      max-height: 500px;
      opacity: 1;
      visibility: visible;
    }

    header::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: rgb(17 24 39 / 0.85); /* bg-gray-900/85 */
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px); /* For Safari */
      opacity: 0;
      transition: opacity 300ms ease-in-out;
    }

    header.is-sticky::before {
      opacity: 1;
    }

    header > nav {
      position: relative;
    }
  `]
})
export class HeaderComponent {
  private themeService = inject(ThemeService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private platformId = inject(PLATFORM_ID);
  translationService = inject(TranslationService);

  isMenuOpen = signal(false);
  isSticky = signal(false);
  isLangPanelOpen = signal(false);
  
  private headerBgImageUrl = this.themeService.headerBgImageUrl;

  headerBgStyle = computed(() => 
    `linear-gradient(rgba(10, 10, 10, 0.6), rgba(10, 10, 10, 0.6)), url('${this.headerBgImageUrl()}')`
  );
  
  navItems = computed(() => this.translationService.t().header.nav);

  constructor() {
    afterNextRender(() => {
        window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        
        // Add global click listener to close the language panel
        window.addEventListener('click', () => {
          this.isLangPanelOpen.set(false);
        });
    });
  }

  toggleMenu(): void {
    this.isMenuOpen.update(value => !value);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  navigateToSection(event: MouseEvent, sectionId: string): void {
    // Okamžite zavrieme menu pre lepší UX na mobile
    this.closeMenu();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    event.preventDefault();
    
    const element = document.querySelector(sectionId);
    if (element) {
      const header = this.elementRef.nativeElement.querySelector('header');
      const headerHeight = header ? header.offsetHeight : 80;
      
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerHeight;
    
      window.scrollTo({
         top: offsetPosition,
         behavior: "smooth"
      });

      // Update URL hash without causing a page jump
      if (history.pushState) {
        history.pushState(null, '', sectionId);
      }
    }
  }

  onScroll(): void {
    const currentScrollY = window.scrollY;
    // Determine sticky state
    this.isSticky.set(currentScrollY > 80);
  }

  // --- Panels ---
  
  toggleLangPanel(event: Event): void {
    event.stopPropagation();
    this.isLangPanelOpen.update(value => !value);
  }

  setLanguage(langCode: LanguageCode): void {
    this.translationService.setLanguage(langCode);
    this.isLangPanelOpen.set(false);
  }

  // Mobile specific language handler
  onMobileLanguageChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const langCode = select.value as LanguageCode;
    this.setLanguage(langCode);
  }
}